import { Model } from '@vuex-orm/core';
import Zaui from '@/include/api';
import { mergeAllLocaleMessages } from '@/i18n';
import $store from '@/store';
import Package from '@/models/Package';
import Product from '@/models/Product';
import moment from 'moment';

export default class Activity extends Model {
  static entity = 'activities';

  get imageUrl() {
    return this.image ? this.image : require('@/assets/images/abstract.png');
  }

  get routeName() {
    return this.activityType === 'p2p' ? 'p2p' : this.type;
  }

  // this is the time shown in summary list
  get listStartTime() {
    return this.activeAvailability ? this.activeAvailability.startTime : null;
  }

  get hasDuration() {
    return moment.duration(this.duration).asHours() || 0;
  }

  get durationParts() {
    return this.duration ? this.duration.split(':') : [0, 0, 0];
  }

  get durationHours() {
    return parseInt(this.durationParts[0], 10); // make sure it's base 10;
  }

  get durationMinutes() {
    return parseInt(this.durationParts[1], 10); // make sure it's base 10;
  }

  get hasLunch() {
    return this.lunches && this.lunches.length;
  }

  get hasPickup() {
    return this.pickups && this.pickups.length;
  }

  get hasCustomRentalTimes() {
    return this.rentalCustomTimes && this.rentalCustomTimes.length;
  }

  // the availability settings are based on the current selected date.
  // this provides seasonality. we could have different availability settings for any given date
  get activeAvailability() {
    return this.filterByActiveDate(moment($store.state.activeDate), this.availability) || {};
  }

  get activeCustomRentalTimes() {
    return this.filterByActiveDate(moment($store.state.activeDate), this.rentalCustomTimes, false) || [];
  }

  get relatedPackages() {
    return this.relatedPackageIds.map(id => {
      return Package.find(id);
    }).filter((item) => item);
  }

  get relatedProducts() {
    return this.relatedProductIds.map(id => {
      return Product.find(id);
    }).filter((item) => item);
  }

  get relatedActivities() {
    return this.relatedActivityIds.map(id => {
      return Activity.find(id);
    }).filter((item) => item);
  }

  filterByActiveDate(d, arr, onlyFirst=true) {
    let active = null;

    if (arr) {
      let found = arr.filter(item => d.isSameOrAfter(item.from) && d.isBefore(item.to));
      if (found.length) {
        active = onlyFirst ? found[0] : found;
      }
    }
    return active;
  }

  static fields() {
    return {
      activityType: this.attr(),
      activityDate: this.attr(),
      category: this.number(),
      description: this.string(),
      duration: this.attr(),
      interval: this.attr(),
      hasGiftCert: this.boolean(),
      id: this.number(),
      image: this.string(),
      lang: this.attr(),
      moreInfo: this.string(),
      name: this.string(),
      notes: this.attr([]),
      pax: this.attr(),
      policies: this.attr(),
      price: this.attr(),
      listPrice: this.attr(),
      rentalPrice: this.attr(),
      shortDescription: this.string(),
      type: this.string(),
      additionalBookingInformation: this.string(),
      tourPolicy: this.string(),
      webSortPriority: this.number(),

      availability: this.attr(),
      dayNumber: this.attr(),
      liveAvailability: this.attr(),
      monthUnavailability: this.attr(),

      pickups: this.attr([]),
      dropoffs: this.attr([]),
      options: this.attr([]),
      lunches: this.attr([]),

      inventory: this.number(),
      inventoryTime: this.attr(),
      inventoryStatus: this.attr(),

      relatedPackageIds: this.attr([]),
      relatedProductIds: this.attr([]),
      relatedActivityIds: this.attr([]),

      rentalCustomTimes: this.attr([]),
      rentalIsMultiDay: this.boolean(false),

      departureTimes: this.attr([]),
      tickets: this.attr([])
    };
  }

  static _handleRelation(id, data, model, dataKey, idKey) {
    if (data[dataKey]) {
      data[idKey] = [];
      data[dataKey].forEach(item => {
        if (!model.find(item.id)){
          if(dataKey == "relatedProducts") {
            model.fetch(item.id, { linkedToTourId: id });
          } else {
            model.fetch(item.id);
          }
        }
        data[idKey].push(item.id);
      });
    }
  }

  static fetch(id, date, activityItem) {
    return Zaui.activity.get(id, date, activityItem).then(data => {
      Activity._handleRelation(id, data, Activity, 'relatedActivities', 'relatedActivityIds');
      Activity._handleRelation(id, data, Package, 'relatedPackages', 'relatedPackageIds');
      Activity._handleRelation(id, data, Product, 'relatedProducts', 'relatedProductIds');
      Activity.insert({ data });
    });
  }

  static fetchList(category, date) {
    return Zaui.activity.list(category, date).then(data => {
      Activity.insert({ data });
    });
  }

  static beforeCreate(model) {
    if (model.lang) {
      mergeAllLocaleMessages(model.lang);
    }
  }
}